import { useState, useEffect, useRef } from 'react';
import { isRunningInIOSApp, setAudioSessionPlayback, setAudioSessionRecord } from '../utils/iosBridge';

interface SpeechRecognitionHook {
  isRecording: boolean;
  spokenText: string;
  spokenWords: string[];
  startRecording: () => void;
  stopRecording: () => void;
  cleanup: () => void;
  acquireStream: () => Promise<boolean>;
  releaseStream: () => Promise<void>;
  isSupported: boolean;
}

export function useSpeechRecognition(
  onResult: (text: string, words: string[], isFinal: boolean) => void,
  onError?: (error: string) => void
): SpeechRecognitionHook & { isInitializing: boolean } {
  const [isRecording, setIsRecording] = useState(false);
  const [spokenText, setSpokenText] = useState('');
  const [spokenWords, setSpokenWords] = useState<string[]>([]);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const recognitionRef = useRef<any>(null);
  const shouldReconnectRef = useRef(false);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const mountedRef = useRef(true);
  const sharedStreamRef = useRef<MediaStream | null>(null);
  const isRecordingRef = useRef(false);
  const isInitializingRef = useRef(false);
  const sessionIdRef = useRef(0);
  const maxReconnectAttempts = 5;
  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onResultRef.current = onResult;
  });

  useEffect(() => {
    onErrorRef.current = onError;
  });

  useEffect(() => {
    mountedRef.current = true;

    const speechRecognitionSupported = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;

    if (!speechRecognitionSupported) {
      console.warn('Speech recognition is not supported on this browser');
      setIsSupported(false);
      onErrorRef.current?.('Speech recognition is not supported on this device or browser');
      return;
    }

    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [onError]);

  const acquireStream = async (): Promise<boolean> => {
    const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (!isMobile) return true;

    if (sharedStreamRef.current) {
      const allLive = sharedStreamRef.current.getTracks().every(t => t.readyState === 'live');
      if (allLive) return true;
      sharedStreamRef.current.getTracks().forEach(t => t.stop());
      sharedStreamRef.current = null;
    }

    try {
      console.log('[SpeechRecognition] Acquiring shared microphone stream');
      if (isRunningInIOSApp()) {
        setAudioSessionRecord();
        await new Promise(r => setTimeout(r, 100));
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      sharedStreamRef.current = stream;
      console.log('[SpeechRecognition] Shared stream acquired');
      return true;
    } catch (err) {
      console.error('[SpeechRecognition] Failed to acquire shared stream:', err);
      onErrorRef.current?.('Please allow microphone access to use speech recognition.');
      return false;
    }
  };

  const releaseStream = (): Promise<void> => {
    return new Promise(resolve => {
      if (!sharedStreamRef.current) {
        if (isRunningInIOSApp()) setAudioSessionPlayback();
        resolve();
        return;
      }

      console.log('[SpeechRecognition] Releasing shared microphone stream');
      const tracks = sharedStreamRef.current.getTracks();
      sharedStreamRef.current = null;

      if (tracks.length === 0) {
        if (isRunningInIOSApp()) setAudioSessionPlayback();
        resolve();
        return;
      }

      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        if (isRunningInIOSApp()) setAudioSessionPlayback();
        resolve();
      };

      let endedCount = 0;
      tracks.forEach(track => {
        track.addEventListener('ended', () => {
          endedCount++;
          if (endedCount === tracks.length) done();
        }, { once: true });
        track.stop();
      });

      // Safety fallback: if tracks don't fire 'ended' within 600ms, resolve anyway
      setTimeout(done, 600);
    });
  };

  const initializeSpeechRecognition = (sessionId: number) => {
    try {
      const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;

      if (recognitionRef.current) {
        try {
          recognitionRef.current.onend = null;
          recognitionRef.current.onerror = null;
          recognitionRef.current.onresult = null;
          recognitionRef.current.abort();
        } catch (e) {
          // Ignore
        }
        recognitionRef.current = null;
      }

      const recognition = new SpeechRecognition();
      const isAndroid = /Android/i.test(navigator.userAgent);

      if (isAndroid) {
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.maxAlternatives = 1;
      } else {
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;
      }

      recognition.lang = 'nl-NL';

      recognition.onerror = (event: any) => {
        if (sessionIdRef.current !== sessionId) return;
        console.log('[SpeechRecognition] Error:', event.error);

        if (event.error === 'not-allowed') {
          setIsSupported(false);
          onErrorRef.current?.('Please allow microphone access to use speech recognition.');
          isRecordingRef.current = false;
          setIsRecording(false);
          shouldReconnectRef.current = false;
          return;
        }

        if (['network', 'service-not-allowed', 'aborted'].includes(event.error)) {
          if (shouldReconnectRef.current && isRecordingRef.current) {
            scheduleReconnect(sessionId);
          }
          return;
        }

        if (event.error === 'no-speech' || event.error === 'audio-capture') {
          if (event.error === 'no-speech') return;
          if (shouldReconnectRef.current && isRecordingRef.current) {
            scheduleReconnect(sessionId);
          }
          return;
        }

        if (shouldReconnectRef.current && isRecordingRef.current) {
          scheduleReconnect(sessionId);
        }
      };

      recognition.onend = () => {
        if (sessionIdRef.current !== sessionId) {
          console.log('[SpeechRecognition] Ignoring stale onend from previous session');
          return;
        }

        const isAndroid = /Android/i.test(navigator.userAgent);

        if (shouldReconnectRef.current && isRecordingRef.current && mountedRef.current && recognitionRef.current) {
          try {
            if (isAndroid) {
              setTimeout(() => {
                if (sessionIdRef.current !== sessionId) return;
                if (shouldReconnectRef.current && isRecordingRef.current && mountedRef.current && recognitionRef.current) {
                  try {
                    recognition.start();
                    reconnectAttemptsRef.current = 0;
                  } catch (error) {
                    scheduleReconnect(sessionId);
                  }
                }
              }, 100);
            } else {
              recognition.start();
              reconnectAttemptsRef.current = 0;
            }
          } catch (error) {
            scheduleReconnect(sessionId);
          }
        } else {
          if (mountedRef.current) {
            isRecordingRef.current = false;
            setIsRecording(false);
          }
        }
      };

      recognition.onresult = (event: any) => {
        if (sessionIdRef.current !== sessionId) return;
        reconnectAttemptsRef.current = 0;

        let interimTranscript = '';
        let finalTranscript = '';
        let isFinal = false;

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript;
            isFinal = true;
          } else {
            interimTranscript += transcript;
          }
        }

        const currentTranscript = (finalTranscript || interimTranscript)
          .toLowerCase()
          .replace(/[.,!?؟;:()'"\-]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();

        const words = currentTranscript
          .split(/\s+/)
          .filter(Boolean)
          .map(word => word.trim().toLowerCase());

        if (mountedRef.current) {
          setSpokenText(currentTranscript);
          setSpokenWords(words);
        }

        onResultRef.current(currentTranscript, words, isFinal);
      };

      recognitionRef.current = recognition;

    } catch (error) {
      console.error('[SpeechRecognition] Error initializing:', error);
      onErrorRef.current?.('Failed to initialize speech recognition');
      setIsSupported(false);
    }
  };

  const scheduleReconnect = (sessionId: number, delay: number = 500) => {
    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    reconnectTimeoutRef.current = window.setTimeout(() => {
      reconnectTimeoutRef.current = null;
      if (sessionIdRef.current !== sessionId) return;

      if (shouldReconnectRef.current && isRecordingRef.current && mountedRef.current && recognitionRef.current !== null) {
        try {
          initializeSpeechRecognition(sessionId);
          if (recognitionRef.current) {
            recognitionRef.current.start();
            reconnectAttemptsRef.current = 0;
          }
        } catch (error) {
          reconnectAttemptsRef.current += 1;
          if (reconnectAttemptsRef.current <= maxReconnectAttempts) {
            scheduleReconnect(sessionId, delay * 1.5);
          } else {
            if (mountedRef.current) {
              isRecordingRef.current = false;
              setIsRecording(false);
              onErrorRef.current?.('Speech recognition failed after multiple attempts. Please try again.');
            }
          }
        }
      }
    }, delay);
  };

  const startRecording = async () => {
    if (!isSupported) {
      onErrorRef.current?.('Speech recognition is not supported on this device');
      return;
    }

    if (isInitializingRef.current || isRecordingRef.current) {
      return;
    }

    const isMobile = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    if (isMobile) {
      isInitializingRef.current = true;
      setIsInitializing(true);

      // Always release any existing stream first on mobile so we get a clean
      // audio session. This prevents iOS from keeping a stale PlayAndRecord
      // session that causes SpeechRecognition.start() to silently fail.
      if (sharedStreamRef.current) {
        console.log('[SpeechRecognition] Releasing stale stream before re-acquire');
        sharedStreamRef.current.getTracks().forEach(t => t.stop());
        sharedStreamRef.current = null;
      }

      // Brief pause to let iOS finalize the old audio session teardown
      await new Promise(resolve => setTimeout(resolve, 120));

      if (!mountedRef.current) {
        isInitializingRef.current = false;
        return;
      }

      const ok = await acquireStream();

      if (!mountedRef.current) {
        isInitializingRef.current = false;
        return;
      }

      if (!ok) {
        isInitializingRef.current = false;
        setIsInitializing(false);
        return;
      }

      // Let iOS fully set up the audio session after getUserMedia returns
      await new Promise(resolve => setTimeout(resolve, 250));

      if (!mountedRef.current) {
        isInitializingRef.current = false;
        return;
      }

      isInitializingRef.current = false;
      setIsInitializing(false);
    }

    startRecognition();
  };

  const startRecognition = () => {
    const sessionId = sessionIdRef.current + 1;
    sessionIdRef.current = sessionId;

    setSpokenText('');
    setSpokenWords([]);
    reconnectAttemptsRef.current = 0;
    shouldReconnectRef.current = true;

    try {
      initializeSpeechRecognition(sessionId);

      if (recognitionRef.current) {
        isRecordingRef.current = true;
        setIsRecording(true);
        recognitionRef.current.start();
        isInitializingRef.current = false;
        if (mountedRef.current) {
          setIsInitializing(false);
        }
      }
    } catch (error) {
      console.error('[SpeechRecognition] Error starting:', error);
      onErrorRef.current?.('Failed to start speech recognition. Please try again.');
      isRecordingRef.current = false;
      isInitializingRef.current = false;
      if (mountedRef.current) {
        setIsRecording(false);
        setIsInitializing(false);
      }
      shouldReconnectRef.current = false;
    }
  };

  const stopRecording = () => {
    if (isRecordingRef.current || isInitializingRef.current || recognitionRef.current) {
      sessionIdRef.current += 1;
    }
    shouldReconnectRef.current = false;
    isRecordingRef.current = false;
    isInitializingRef.current = false;

    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (recognitionRef.current) {
      try {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.onstart = null;
        recognitionRef.current.onspeechend = null;
        recognitionRef.current.onaudiostart = null;
        recognitionRef.current.onaudioend = null;
        recognitionRef.current.onsoundstart = null;
        recognitionRef.current.onsoundend = null;
      } catch (err) {
        // Ignore
      }

      try {
        recognitionRef.current.abort();
      } catch (err) {
        // Ignore
      }

      recognitionRef.current = null;
    }

    reconnectAttemptsRef.current = 0;

    if (mountedRef.current) {
      setIsRecording(false);
      setIsInitializing(false);
      setSpokenText('');
      setSpokenWords([]);
    }
  };

  const cleanup = () => {
    stopRecording();
    releaseStream();
  };


  return {
    isRecording,
    spokenText,
    spokenWords,
    startRecording,
    stopRecording,
    cleanup,
    acquireStream,
    releaseStream,
    isSupported,
    isInitializing
  };
}
